import {
  GUIDE_CATEGORIES, getGuideArticle, getGuideCategory, getAllGuideArticles,
  getArticlesByCategory, getCategoryForArticle, getRelatedArticles,
  type GuideArticle, type GuideCategory,
} from "./index";
import { isKnowledgeObject } from "@/lib/public-knowledge-template";

export interface GuideDirectoryView {
  categories: GuideCategory[];
  category?: GuideCategory;
  articles: GuideArticle[];
  categoryArticleSlugs: Record<string, string[]>;
}
export interface GuideArticleView {
  article: GuideArticle;
  category?: GuideCategory;
  related: GuideArticle[];
}
export function buildGuideDirectoryView(slug?: string): GuideDirectoryView | null {
  const category = slug ? getGuideCategory(slug) : undefined;
  if (slug && !category) return null;
  const categories = category ? [category] : GUIDE_CATEGORIES;
  return {
    categories, ...(category ? { category } : {}),
    articles: slug ? getArticlesByCategory(slug) : getAllGuideArticles(),
    categoryArticleSlugs: Object.fromEntries(categories.map(item =>
      [item.slug, getArticlesByCategory(item.slug).map(article => article.slug)])),
  };
}
export function buildGuideArticleView(slug: string): GuideArticleView | null {
  const article = getGuideArticle(slug);
  return article ? { article, category: getCategoryForArticle(article), related: getRelatedArticles(article) } : null;
}
const categoryShape = (value: unknown) => isKnowledgeObject(value) &&
  ["slug", "title", "description", "metaTitle", "metaDescription"].every(key => typeof value[key] === "string") &&
  isKnowledgeObject(value.hero) && typeof value.hero.image === "string";
const articleShape = (value: unknown) => isKnowledgeObject(value) &&
  ["slug", "category", "title", "excerpt", "metaTitle", "metaDescription", "author", "publishedAt", "updatedAt"]
    .every(key => typeof value[key] === "string") &&
  isKnowledgeObject(value.hero) && typeof value.hero.image === "string" &&
  Array.isArray(value.body) && value.body.every(isKnowledgeObject) &&
  Array.isArray(value.faqs) && Array.isArray(value.commercialLinks);
export function isGuideDirectoryView(value: unknown): value is GuideDirectoryView {
  return isKnowledgeObject(value) && Array.isArray(value.categories) &&
    value.categories.every(categoryShape) && Array.isArray(value.articles) &&
    value.articles.every(articleShape) && isKnowledgeObject(value.categoryArticleSlugs) &&
    value.categories.every(category => {
      const slugs = (value.categoryArticleSlugs as Record<string, unknown>)[category.slug];
      return Array.isArray(slugs) && slugs.every(slug => typeof slug === "string" &&
        (value.articles as GuideArticle[]).some(article => article.slug === slug));
    }) && (value.category === undefined || categoryShape(value.category));
}
export function isGuideArticleView(value: unknown): value is GuideArticleView {
  return isKnowledgeObject(value) && articleShape(value.article) &&
    Array.isArray(value.related) && value.related.every(articleShape) &&
    (value.category === undefined || categoryShape(value.category));
}
